/**
 * A generic, single-object-store IndexedDB queue with an in-memory fallback.
 *
 * This is the SAME pattern as `outboxStore.ts` (the of-002/of-003 entry
 * outbox), factored out so the two new offline queues this file's callers
 * need — held receipt photos (`scanQueue.ts`) and pending vendor-mapping
 * writes (`mappingQueue.ts`) — don't hand-copy that IndexedDB boilerplate a
 * second and third time. `outboxStore.ts` itself is left untouched: it is
 * already gated and working, and touching it is not worth the regression
 * risk for what is otherwise a mechanical extraction.
 */

export interface QueueItem {
  clientId: string;
  queuedAt: number;
}

export interface PersistentQueue<T extends QueueItem> {
  add(item: T): Promise<void>;
  remove(clientId: string): Promise<void>;
  all(): Promise<T[]>;
}

function openDb(dbName: string, storeName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: "clientId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error(`IndexedDB open failed for ${dbName}`));
    req.onblocked = () => reject(new Error(`IndexedDB open blocked for ${dbName}`));
  });
}

class IndexedDbQueue<T extends QueueItem> implements PersistentQueue<T> {
  constructor(
    private readonly db: IDBDatabase,
    private readonly storeName: string,
  ) {}

  add(item: T): Promise<void> {
    return this.run("readwrite", (store) => store.put(item));
  }

  remove(clientId: string): Promise<void> {
    return this.run("readwrite", (store) => store.delete(clientId));
  }

  all(): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, "readonly");
      const req = tx.objectStore(this.storeName).getAll();
      req.onsuccess = () => resolve((req.result as T[]).slice().sort((a, b) => a.queuedAt - b.queuedAt));
      req.onerror = () => reject(req.error ?? new Error("IndexedDB read failed"));
    });
  }

  private run(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, mode);
      const req = op(tx.objectStore(this.storeName));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error("IndexedDB write failed"));
    });
  }
}

/** In-memory fallback — same rationale as `outboxStore.ts`'s `MemoryOutboxStore`:
 *  Safari private browsing can have `indexedDB` present but non-functional, and a
 *  storage failure must never surface as an error to the visitor. */
class MemoryQueue<T extends QueueItem> implements PersistentQueue<T> {
  private items = new Map<string, T>();

  async add(item: T): Promise<void> {
    this.items.set(item.clientId, item);
  }

  async remove(clientId: string): Promise<void> {
    this.items.delete(clientId);
  }

  async all(): Promise<T[]> {
    return [...this.items.values()].sort((a, b) => a.queuedAt - b.queuedAt);
  }
}

/**
 * Picks IndexedDB when a real write actually round-trips, memory otherwise —
 * see `outboxStore.ts`'s `createOutboxStore` for the full rationale (the
 * "verify the verification" instinct: presence of `window.indexedDB` is not
 * proof it works in Safari private browsing).
 */
export async function createPersistentQueue<T extends QueueItem>(
  dbName: string,
  storeName: string,
  probeItem: T,
): Promise<PersistentQueue<T>> {
  try {
    if (typeof indexedDB === "undefined") throw new Error("indexedDB unavailable");
    const db = await openDb(dbName, storeName);
    const store = new IndexedDbQueue<T>(db, storeName);
    await store.add(probeItem);
    await store.remove(probeItem.clientId);
    return store;
  } catch (err) {
    console.warn(
      `IndexedDB is unavailable for ${dbName} (likely private browsing) — this queue will ` +
        "hold its items in memory for this tab only and will not survive a reload.",
      err,
    );
    return new MemoryQueue<T>();
  }
}
